import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Alert, Row } from 'reactstrap';

import useAlerts from '../../../../hooks/order/useAlerts';
import globalTranslations from '../../../../i18n/globalTranslations';
import Order from '../../../../models/order/Order';
import { modelOf } from '../../../../prop-types';
import ConfigStore from '../../../../store/ConfigStore';
import OrderType from '../../../../types/order/OrderType';
import ProductClass from '../../../../types/ProductClass';
import getOrderProductRowKey from '../../../../util/orderProductRowKey';
import CustomCol from '../../../bootstrap/CustomCol';
import Icon from '../../../common/Icon';
import Price from '../../../product/Price';
import OrderTrackingLink from '../OrderTrackingLink';

const messages = defineMessages({
  paymentInstructions: {
    id: 'checkout.paymentInformation',
    defaultMessage:
      'Confirmation email has also been sent to the email address {email}. Make sure email address is correct.',
  },
  paymentInstructions2: {
    id: 'checkout.paymentInformation2',
    defaultMessage:
      "If you don't see the message, be sure to check your spam folder as well.",
  },
});

const CheckoutConfirmation = ({ configStore, currentOrder, orderType }) => {
  const { commonAlerts } = currentOrder;

  const { renderGeneralAlerts } = useAlerts({
    generalAlerts: commonAlerts.general,
  });

  const renderSuccessPage = () => {
    return (
      <CustomCol xs={12} md={10} xxl={7} className="CheckoutConfirmation">
        {renderConfirmation()}
      </CustomCol>
    );
  };

  const renderConfirmation = () => {
    return (
      <>
        {renderSuccessInformation()}
        {configStore.account.enableMyAccount && renderTrackingInformation()}
        {renderGeneralAlerts()}
        {renderInformation()}
      </>
    );
  };

  const renderSuccessInformation = () => {
    const { confirmation_email } = currentOrder;

    const successTitle =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage {...globalTranslations.successfulPurchasePageTitle} />
      ) : (
        <FormattedMessage
          {...globalTranslations.successfulProposalReceivedTitle}
        />
      );

    return (
      <>
        {renderContentRow({ xs: 12 }, <Icon name="check" />, {
          className: 'CheckoutConfirmation__order-success-icon',
        })}
        {renderContentRow({ xs: 12 }, successTitle, {
          className: 'CheckoutConfirmation__order-completed',
        })}
        {confirmation_email &&
          renderContentRow(
            { xs: 12 },
            <FormattedMessage
              {...messages.paymentInstructions}
              values={{
                email: confirmation_email,
              }}
            />,
            {
              className: 'CheckoutConfirmation__payment-instructions',
            }
          )}
        {confirmation_email &&
          renderContentRow(
            { xs: 12 },
            <FormattedMessage {...messages.paymentInstructions2} />,
            {
              className: 'CheckoutConfirmation__payment-instructions',
            }
          )}
      </>
    );
  };

  const renderTrackingInformation = () => {
    const paymentMethodMessages = currentOrder.payment_method_messages;

    return renderContentRow(
      { xs: 12 },
      <div>
        <OrderTrackingLink currentOrder={currentOrder} />
        <div className="CheckoutConfirmation__payment-method-messages">
          {paymentMethodMessages &&
            paymentMethodMessages.length > 0 &&
            paymentMethodMessages.map((message, index) =>
              renderAlert('warning', <span>{message}</span>, {
                key: index,
              })
            )}
        </div>
      </div>,
      {
        className: 'CheckoutConfirmation__order-confirmation-message',
      }
    );
  };

  const renderInformation = () => {
    const header =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage {...globalTranslations.orderInfoHeader} />
      ) : (
        <FormattedMessage {...globalTranslations.offerInfoHeader} />
      );
    const confirmation =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage {...globalTranslations.orderConfirmationThankYou} />
      ) : (
        <FormattedMessage {...globalTranslations.offerConfirmationThankYou} />
      );

    return (
      <>
        {renderContentRow({ xs: 12 }, header, {
          className: 'CheckoutConfirmation__order-information-title',
        })}
        {renderContentRow({ xs: 12 }, renderInformationContent())}
        {renderContentRow(
          { xs: 12 },
          <div className="CheckoutConfirmation__order-information-thankyou">
            {confirmation}
          </div>
        )}
      </>
    );
  };

  const renderOrderedQuantity = (product) => {
    if (product.quantity_ordered) {
      return <> x {product.quantity_ordered}</>;
    }

    return null;
  };

  const renderInformationContent = () => {
    const { paymentMethod } = currentOrder;
    const { shippingMethod } = currentOrder;
    const id = currentOrder.id;
    const paymentMethodName = paymentMethod && paymentMethod.name;
    const shippingMethodName = shippingMethod && shippingMethod.name;
    const pickupPoint = currentOrder.pickupPoint || null;
    const orderNumberText =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage {...globalTranslations.orderNumber} />
      ) : (
        <FormattedMessage {...globalTranslations.offerNumber} />
      );

    const confirmationProducts = currentOrder.currentOrderProducts;
    const structureBundleIds = new Set(
      confirmationProducts
        .filter((p) => p.class === ProductClass.STRUCTURE && !!p.bundle_id)
        .map((p) => p.bundle_id)
    );
    const isStructureChildProduct = (product) =>
      !!product.bundle_id &&
      structureBundleIds.has(product.bundle_id) &&
      product.class !== ProductClass.STRUCTURE;
    const renderProductName = (product) => (
      <>
        {currentOrder.show_product_code && product.productCode
          ? `${product.productCode} - `
          : null}
        {product.name}
      </>
    );

    return (
      <div className="CheckoutConfirmation__order-information-content">
        {renderContentRow(
          { xs: 12 },
          <div className="CheckoutConfirmation__order-information-product-wrapper">
            {confirmationProducts.map((product) => (
              <div
                key={getOrderProductRowKey(product)}
                className={classNames(
                  'CheckoutConfirmation__order-information-product',
                  {
                    'CheckoutConfirmation__order-information-product--structure-child':
                      isStructureChildProduct(product),
                  }
                )}
              >
                <span>
                  {renderProductName(product)}
                  {renderOrderedQuantity(product)}
                </span>
                <span className="CheckoutConfirmation__order-information-value">
                  {currentOrder.allTotals.length > 0 && (
                    <Price price={product.unit_price_formatted} />
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
        {currentOrder.allTotals.length > 0 &&
          renderContentRow(
            { xs: 12 },
            <div className="CheckoutConfirmation__order-information-total-wrapper">
              {currentOrder.allTotals.map((total) => (
                <div
                  key={total.class}
                  className="CheckoutConfirmation__order-information-total"
                >
                  <span>{total.name}</span>
                  <span
                    className="CheckoutConfirmation__order-information-value"
                    dangerouslySetInnerHTML={{
                      __html: total.text,
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        {renderContentRow(
          { xs: 12 },
          <div className="CheckoutConfirmation__order-information-id">
            {orderNumberText}
            <span className="CheckoutConfirmation__order-information-value">
              {id}
            </span>
          </div>
        )}
        {paymentMethodName &&
          renderContentRow(
            { xs: 12 },
            <div className="CheckoutConfirmation__order-information-payment-method">
              <FormattedMessage {...globalTranslations.paymentMethod} />
              <span className="CheckoutConfirmation__order-information-value">
                {paymentMethodName}
              </span>
            </div>
          )}
        {shippingMethodName &&
          renderContentRow(
            { xs: 12 },
            <div className="CheckoutConfirmation__order-information-shipping-method">
              <FormattedMessage {...globalTranslations.shippingMethod} />
              <span className="CheckoutConfirmation__order-information-value">
                {shippingMethodName}
              </span>
            </div>
          )}
        {pickupPoint &&
          renderContentRow(
            { xs: 12 },
            <div className="CheckoutConfirmation__order-information-pickup-point">
              <FormattedMessage {...globalTranslations.pickupPointTitle} />
              <div>
                <div className="CheckoutConfirmation__order-information-value">
                  {pickupPoint.name}
                </div>
                <div className="CheckoutConfirmation__order-information-value">
                  {pickupPoint.address}
                </div>
              </div>
            </div>
          )}
      </div>
    );
  };

  const renderContentRow = (sizes, content, className, rest) => (
    <Row {...rest} {...className}>
      <CustomCol {...sizes}>{content}</CustomCol>
    </Row>
  );

  const renderAlert = (type, message, rest) => (
    <Alert {...rest} color={type}>
      {message}
    </Alert>
  );

  return <>{renderSuccessPage()}</>;
};

CheckoutConfirmation.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  currentOrder: modelOf(Order).isRequired,
  orderType: PropTypes.string,
};

export default inject('configStore')(observer(CheckoutConfirmation));
